import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AddIcon from '@mui/icons-material/Add';
import GridViewIcon from '@mui/icons-material/GridView';
import InsightsIcon from '@mui/icons-material/Insights';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloseIcon from '@mui/icons-material/Close';
import { Tooltip } from '@mui/material';
import axiosInstance from '../api';
import ThemeModeToggle from './ThemeModeToggle';
import { useThemeMode } from '../theme/ThemeModeProvider';
import { dsVar } from '../theme/tokens';
import { invalidateProjectList, setStoredProjectId } from '../services/projectService';

interface SidebarProps {
    activePage?: 'hub' | 'projects' | 'marketplace' | 'ontology' | 'activity-monitor' | 'linked-data';
    showBackButton?: boolean;
    /**
     * Collapse the sidebar regardless of the user's own toggle — used while a
     * docked drawer is open to give the page more room. Their preference is kept
     * in local state and restored as soon as this goes false.
     */
    forceCollapsed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ activePage, showBackButton, forceCollapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { resolved: resolvedTheme } = useThemeMode();

    /**
     * The user's own collapse preference, remembered across navigations.
     *
     * This used to be re-derived from the path on every navigation, which
     * pinned the rail closed on every project page. Now that a project page is
     * where sign-in lands, that would be the permanent state of the app and the
     * project sub-items below would never be visible. The flow editor still
     * opens collapsed, but only as a starting point the user can override.
     */
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const stored = localStorage.getItem('sidebar_collapsed');
        if (stored !== null) return stored === 'true';
        return location.pathname.includes('/AIStudio/customize');
    });

    // New project modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [projectName, setProjectName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * The project the user is currently inside, if any.
     *
     * Taken from the URL rather than from state: the route is the only place a
     * current project is recorded anywhere in the app.
     */
    const projectMatch = location.pathname.match(/^\/analytics\/projects\/([^/]+)/);
    const currentProjectId = projectMatch?.[1];

    /**
     * Where a project's own screens hang. Shown under 'Your Workspace' only
     * while a project is open, so Case operations is reachable from the chrome
     * rather than only from the band on the project page.
     */
    const projectNavItems = currentProjectId
        ? [
            {
                id: 'overview',
                label: 'Overview',
                icon: <InsightsIcon fontSize="small" />,
                path: `/analytics/projects/${currentProjectId}`,
                active: location.pathname === `/analytics/projects/${currentProjectId}`,
            },
            {
                id: 'case-operations',
                label: 'Case operations',
                icon: <FactCheckIcon fontSize="small" />,
                path: `/analytics/projects/${currentProjectId}/case-workflow`,
                active: location.pathname.endsWith('/case-workflow'),
            },
        ]
        : [];

    const toggleSidebar = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        localStorage.setItem('sidebar_collapsed', String(next));
    };

    // What the UI renders. A forced collapse wins, but the user's own choice stays
    // in `isCollapsed` underneath and takes effect again the moment it lifts.
    const collapsed = isCollapsed || Boolean(forceCollapsed);

    const openModal = () => {
        setProjectName('');
        setError(null);
        setModalOpen(true);
    };

    const closeModal = () => {
        if (submitting) return;
        setModalOpen(false);
        setError(null);
    };

    const handleCreateProject = async () => {
        const trimmedName = projectName.trim();
        if (!trimmedName) {
            setError('Project name is required.');
            return;
        }
        try {
            setSubmitting(true);
            setError(null);
            const orgId = localStorage.getItem('org_id');
            const response = await axiosInstance.post('/projects', {
                projectName: trimmedName,
                orgId,
            });
            const created = response.data?.project || response.data;
            const projectId = created?._id;
            // The cached list predates this project, and a project just created
            // is the one the user means to work in next.
            invalidateProjectList();
            if (projectId) setStoredProjectId(projectId);
            setModalOpen(false);
            navigate(`/analytics/projects/${projectId}`);
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to create project. Please try again.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const navItems = [
        {
            id: 'hub',
            label: 'Your Workspace',
            icon: <HubIcon />,
            // Resolves to the user's default project rather than the grid of
            // all of them; the grid is 'All projects' below.
            path: '/workspace',
            active: activePage === 'hub',
        },
        {
            id: 'projects',
            label: 'All projects',
            icon: <GridViewIcon />,
            path: '/intelligence-hub',
            active: activePage === 'projects',
        },
        {
            id: 'marketplace',
            label: 'Agentic Hub',
            icon: <StorefrontIcon />,
            path: '/operators-marketplace',
            active: activePage === 'marketplace',
        },
        {
            id: 'ontology',
            // Covers the risk library and the thresholds as well as the policies, which
            // is what the page behind it has always actually held.
            label: 'Policy & Risk',
            icon: <MenuBookIcon />,
            path: '/ontology',
            active: activePage === 'ontology',
        },
        {
            id: 'linked-data',
            label: 'Linked Data/ Apps',
            icon: <SettingsInputComponentIcon />,
            path: '/linked-data-apps',
            active: activePage === 'linked-data',
        },
        {
            id: 'activity-monitor',
            label: 'Activity Monitor',
            icon: <MonitorHeartIcon />,
            path: '/monitoringDashboard',
            active: activePage === 'activity-monitor',
        },
    ];

    return (
        <>
            {/* Pinned to the chosen theme, so legacy pages (always painted light) don't flip the rail. */}
            <aside
                data-theme={resolvedTheme}
                className={`${collapsed ? 'w-20' : 'w-64'} border-r border-line bg-page flex flex-col shrink-0 transition-all duration-300 relative group`}
            >
                {/* Collapse Toggle Button — hidden while forced, where it would be a dead control */}
                {!forceCollapsed && (
                    <div className="absolute -right-3 top-20 z-50">
                        <button
                            onClick={toggleSidebar}
                            className="w-6 h-6 rounded-full bg-surface border border-line text-accent-fg flex items-center justify-center cursor-pointer hover:bg-line transition-colors"
                        >
                            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                        </button>
                    </div>
                )}

                <div className={`p-6 flex-1 flex flex-col ${collapsed ? 'items-center' : ''}`}>
                    {/* Logo */}
                    <div className={`flex items-center gap-3 mb-6 ${collapsed ? 'justify-center' : ''}`}>
                        <h1 className="font-mono text-lg tracking-tighter m-0 text-accent-fg">
                            {collapsed ? (
                                <span className="text-accent-fg">[S]</span>
                            ) : (
                                <>
                                    <span className="text-fg">Data</span>
                                    <span className="text-accent-fg">[S]</span>
                                    <span className="text-fg">cient</span>
                                    <span className="text-accent-fg">.ai</span>
                                </>
                            )}
                        </h1>
                    </div>

                    {/* ── Start CTA ── */}
                    <Tooltip title={collapsed ? 'Start a new project' : ''} placement="right">
                        <button
                            onClick={openModal}
                            className={`w-full hover:brightness-110 text-on-accent font-bold rounded-xl flex items-center justify-center transition-all whitespace-nowrap border-none cursor-pointer mb-6 bg-accent shadow-lg shadow-accent/20 ${collapsed ? 'p-0 w-10 h-10' : 'gap-2 py-3 px-4'
                                }`}
                        >
                            <RocketLaunchIcon fontSize="small" />
                            {!collapsed && <span>Start</span>}
                        </button>
                    </Tooltip>

                    {/* Back Button (Optional) */}
                    {showBackButton && (
                        <button
                            onClick={() => navigate('/workspace')}
                            className={`w-full bg-surface hover:bg-raised text-fg-soft font-medium rounded-lg flex items-center mb-6 transition-colors border border-line cursor-pointer ${collapsed ? 'justify-center p-2' : 'gap-2 py-2.5 px-4'}`}
                        >
                            <ArrowBackIcon fontSize="small" />
                            {!collapsed && <span>Back to Workspace</span>}
                        </button>
                    )}

                    {/* Navigation Links */}
                    <nav className="space-y-1 w-full">
                        {navItems.map((item) => (
                            <div key={item.id}>
                                {collapsed ? (
                                    <Tooltip title={item.label} placement="right">
                                        <button
                                            onClick={() => navigate(item.path)}
                                            className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors border-none bg-transparent cursor-pointer mx-auto ${item.active ? 'text-accent-fg bg-accent/15' : 'text-fg-2 hover:bg-surface hover:text-fg'}`}
                                        >
                                            {item.icon}
                                        </button>
                                    </Tooltip>
                                ) : (
                                    <button
                                        onClick={() => navigate(item.path)}
                                        className={`flex items-center gap-3 px-3 py-2 w-full rounded-lg transition-colors border-none bg-transparent cursor-pointer text-left ${item.active ? 'bg-accent/15 text-accent-fg font-medium' : 'text-fg-2 hover:bg-surface hover:text-fg'}`}
                                    >
                                        {item.icon}
                                        <span className="no-underline">{item.label}</span>
                                    </button>
                                )}

                                {/* A project's own screens, indented under the workspace entry. */}
                                {item.id === 'hub' && !collapsed && projectNavItems.map((sub) => (
                                    <button
                                        key={sub.id}
                                        onClick={() => navigate(sub.path)}
                                        className={`flex items-center gap-2 pl-10 pr-3 py-1.5 w-full rounded-lg transition-colors border-none bg-transparent cursor-pointer text-left text-sm ${sub.active ? 'bg-accent/15 text-accent-fg font-medium' : 'text-fg-muted hover:bg-surface hover:text-fg'}`}
                                    >
                                        {sub.icon}
                                        <span className="no-underline">{sub.label}</span>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </nav>
                </div>

                {/* Bottom Section */}
                <div className={`mt-auto p-6 border-t border-line ${collapsed ? 'flex flex-col items-center gap-4' : ''}`}>
                    <Tooltip title={collapsed ? 'Settings' : ''} placement="right">
                        <button className={`flex items-center rounded-lg text-fg-2 hover:bg-surface hover:text-fg transition-colors border-none bg-transparent cursor-pointer w-full ${collapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2'}`}>
                            <SettingsIcon />
                            {!collapsed && <span>Settings</span>}
                        </button>
                    </Tooltip>

                    <div className={collapsed ? '' : 'mt-2 px-3'}>
                        <ThemeModeToggle compact={collapsed} />
                    </div>

                    <div className={`flex items-center mt-2 ${collapsed ? 'justify-center' : 'gap-3 px-3 py-4'}`}>
                        <div className="w-8 h-8 rounded-full bg-line flex items-center justify-center text-xs font-bold border border-accent/30 shrink-0">JD</div>
                        {!collapsed && (
                            <div className="text-sm border-0 border-white m-0 p-0 text-left w-auto leading-normal">
                                <p className="font-medium m-0">Jane Doe</p>
                                <p className="text-xs text-fg-muted m-0">Pro Plan</p>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* ── New project modal (rendered outside <aside> to escape overflow clipping) ── */}
            {modalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center"
                    style={{ backgroundColor: dsVar('page', 0.85), backdropFilter: 'blur(6px)' }}
                    onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    <div
                        className="w-full max-w-md bg-surface border border-line rounded-2xl shadow-2xl p-8 flex flex-col gap-6"
                        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.6)' }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between m-0">
                            <h2 className="font-mono text-xl m-0 text-accent-fg">New project</h2>
                            <button
                                onClick={closeModal}
                                disabled={submitting}
                                className="p-1 text-fg-2 hover:text-fg transition-colors bg-transparent border-none cursor-pointer flex items-center justify-center rounded-lg hover:bg-raised disabled:opacity-50"
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        </div>

                        {/* Input */}
                        <div className="flex flex-col gap-2 m-0">
                            <label className="text-xs font-medium text-fg-2 uppercase tracking-widest m-0">
                                Project name
                            </label>
                            <input
                                autoFocus
                                type="text"
                                value={projectName}
                                onChange={(e) => { setProjectName(e.target.value); setError(null); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCreateProject();
                                    if (e.key === 'Escape') closeModal();
                                }}
                                placeholder="e.g. My New Marketing Project"
                                disabled={submitting}
                                className="w-full bg-page border border-line rounded-lg py-3 px-4 text-sm text-fg placeholder:text-fg-faint focus:outline-none focus:ring-1 focus:ring-accent transition-all disabled:opacity-50"
                            />
                            {error && (
                                <p className="text-red-400 text-xs m-0 mt-1">{error}</p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 justify-end m-0">
                            <button
                                onClick={closeModal}
                                disabled={submitting}
                                className="px-5 py-2.5 rounded-lg text-sm font-medium text-fg-soft bg-transparent border border-line hover:border-fg-2 transition-colors cursor-pointer disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateProject}
                                disabled={submitting || !projectName.trim()}
                                className="px-5 py-2.5 rounded-lg text-sm font-bold text-on-accent bg-accent hover:brightness-110 transition-all border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <span className="inline-block w-3.5 h-3.5 border-2 border-on-accent/40 border-t-on-accent rounded-full animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <AddIcon fontSize="small" />
                                        Create project
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Sidebar;
